import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { AppState, useAppDispatch } from 'stores/type';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';
import { useIntl } from 'react-intl';
import { localeIds } from 'locals';
import { DeviceDomain } from 'models/Device';
import { DistributionDomain } from 'models/Distribution';
import { Application } from 'models/Application/type';
import { Splash } from 'views/components/molecules/Splash';
import { DefaultTemplate } from 'views/templates/DefaultTemplate';
import { DistributionDetailPageLayout } from 'views/layouts/DistributionDetailPageLayout';
import { setErrorMessageLocaleId, setExceptionError } from 'stores/errorSlice';
import { MONTHLY_TRANSFER_LIMIT_EXCEEDED_MESSAGE } from 'repositories/Error';
import { showDialog, closeDialog } from 'stores/dialogSlice';
import { ROUTER_PATH } from 'views/constants';
import { getRequestPath } from 'utils/string';
import { useDevice } from 'views/hooks/useDevice';

interface DistributionDetailPageParams {
  distributionAccessKey: string;
}

export const DistributionDetailPage: React.FC = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const useSelector: TypedUseSelectorHook<AppState> = useReduxSelector;
  const device = useSelector((state) => state.deviceState.device);
  const { reInstallConfigurationProfile } = useDevice();
  const deviceDomain = new DeviceDomain(device);
  const { distributionAccessKey } = useParams<DistributionDetailPageParams>();
  const distributionDomain = useMemo(() => {
    return new DistributionDomain(distributionAccessKey, device);
  }, [device, distributionAccessKey]);
  const [application, setApplication] = useState<Application | undefined>(
    undefined
  );
  const [startInstall, setStartInstall] = useState(false);
  const [startOverwriteInstall, setStartOverwriteInstall] = useState(false);
  const [displayName, setDisplayName] = useState<string | undefined>(undefined);
  const [notification, setNotification] = useState<boolean | undefined>(
    undefined
  );
  const [installSessionToken, setInstallSessionToken] = useState<
    string | undefined
  >(undefined);
  const intl = useIntl();

  const handleCloseDialogAndGoBack = useCallback(() => {
    dispatch(closeDialog());
    history.goBack();
  }, [history, dispatch]);

  const handleCloseDialog = useCallback(() => {
    dispatch(closeDialog());
  }, [dispatch]);

  const handleAlertWithGoBack = useCallback(
    (id: string, values?: Record<string, string>) => {
      dispatch(
        showDialog({
          content: intl.formatMessage({ id }, values),
          defaultAction: {
            label: intl.formatMessage({ id: localeIds.dialog.ok }),
            callback: handleCloseDialogAndGoBack,
          },
        })
      );
    },
    [dispatch, handleCloseDialogAndGoBack, intl]
  );

  useEffect(() => {
    // インストールボタンタップ時にセッションを作ると不要なダイアログ(iTunesを開きますか)が出るので、
    // そのダイアログが出ないようにワークアラウンドで画面表示にセッションを作る
    distributionDomain
      .installSessionToken()
      .then((result) => {
        if (result.isFailure()) {
          // 月間転送量上限エラーの場合はページに留まる
          const callback =
            result.value.originalMessage ===
            MONTHLY_TRANSFER_LIMIT_EXCEEDED_MESSAGE
              ? handleCloseDialog
              : handleCloseDialogAndGoBack;
          dispatch(
            showDialog({
              content: intl.formatMessage({ id: result.value.localeId() }),
              defaultAction: {
                label: intl.formatMessage({ id: localeIds.dialog.ok }),
                callback,
              },
            })
          );
          return;
        }
        setInstallSessionToken(result.value);
      })
      .catch((e) => {
        dispatch(setExceptionError(e));
      });

    distributionDomain
      .detail()
      .then((result) => {
        if (result.isFailure()) {
          dispatch(
            showDialog({
              content: intl.formatMessage({ id: result.value.localeId() }),
              defaultAction: {
                label: intl.formatMessage({ id: localeIds.dialog.ok }),
                callback: handleCloseDialogAndGoBack,
              },
            })
          );
          return;
        }

        const [application, distribution] = result.value;
        setApplication(application);
        setDisplayName(distribution.displayName);
        setNotification(distribution.notification);
      })
      .catch((e) => {
        dispatch(setExceptionError(e));
      });
  }, [
    dispatch,
    distributionAccessKey,
    distributionDomain,
    handleCloseDialog,
    handleCloseDialogAndGoBack,
    intl,
  ]);

  const install = useCallback(
    async (overwrite: boolean): Promise<void> => {
      distributionDomain
        .install(installSessionToken as string, overwrite)
        .then((result) => {
          if (result.isFailure()) {
            dispatch(setErrorMessageLocaleId(result.value.localeId()));
            return;
          }

          dispatch(
            showDialog({
              content: intl.formatMessage({
                id: localeIds.alert.startBinaryInstall,
              }),
            })
          );
          if (overwrite || device?.detail?.mustOverwriteInstall) {
            setStartOverwriteInstall(true);
          } else {
            setStartInstall(true);
          }
        })
        .catch((e) => {
          dispatch(setExceptionError(e));
        });
    },
    [device, dispatch, distributionDomain, installSessionToken, intl]
  );

  const handleInstall = useCallback(() => {
    install(false);
  }, [install]);

  const handleOverwriteInstall = useCallback(() => {
    install(true);
  }, [install]);

  const handleUnsupportedOverwriteInstall = useCallback(() => {
    if (device?.detail) {
      handleAlertWithGoBack(localeIds.appInstall.unsupportedOverwriteInstall, {
        deviceOsVersion: device.detail.osVersion,
      });
    }
  }, [device, handleAlertWithGoBack]);

  const handleChangeDisplayName = useCallback(
    (currentName: string) => {
      const displayName = prompt(
        intl.formatMessage({
          id: localeIds.prompt.inputDisplayName,
        }),
        currentName
      );

      if (displayName !== null && displayName !== '') {
        distributionDomain
          .updateDisplayName(displayName)
          .then((result) => {
            if (result.isFailure()) {
              dispatch(setErrorMessageLocaleId(result.value.localeId()));
              return;
            }

            setDisplayName(result.value);
          })
          .catch((e) => {
            dispatch(setExceptionError(e));
          });
      }
    },
    [dispatch, distributionDomain, intl]
  );

  const leaveAndGoApp = useCallback(() => {
    handleCloseDialog();
    distributionDomain.leave().then(() => {
      history.push(ROUTER_PATH.apps);
    });
  }, [distributionDomain, handleCloseDialog, history]);

  const handleLeave = useCallback(() => {
    dispatch(
      showDialog({
        content: intl.formatMessage({ id: localeIds.confirm.leaveTest }),
        defaultAction: {
          label: intl.formatMessage({ id: localeIds.dialog.ok }),
          callback: leaveAndGoApp,
        },
        cancelAction: {
          label: intl.formatMessage({ id: localeIds.dialog.cancel }),
          callback: handleCloseDialog,
        },
      })
    );
  }, [dispatch, handleCloseDialog, intl, leaveAndGoApp]);

  const handleChangeNotification = useCallback(
    (enable: boolean) => {
      distributionDomain
        .setNotification(enable)
        .then((result) => {
          if (result.isFailure()) {
            dispatch(setErrorMessageLocaleId(result.value.localeId()));
            return;
          }

          setNotification(enable);
        })
        .catch((e) => {
          dispatch(setExceptionError(e));
        });
    },
    [dispatch, distributionDomain]
  );

  const reInstallProfile = useCallback(() => {
    dispatch(
      showDialog({
        content: intl.formatMessage({
          id: localeIds.configurationProfileInfo.confirm.reInstall,
        }),
        defaultAction: {
          label: intl.formatMessage({ id: localeIds.dialog.ok }),
          callback: async () => {
            dispatch(closeDialog());
            await reInstallConfigurationProfile().catch((e: Error) => {
              dispatch(setExceptionError(e));
            });
          },
        },
        cancelAction: {
          label: intl.formatMessage({ id: localeIds.dialog.cancel }),
          callback: handleCloseDialog,
        },
      })
    );
  }, [dispatch, handleCloseDialog, intl, reInstallConfigurationProfile]);

  const handleReInstallProfile = React.useCallback(() => {
    reInstallProfile();
  }, [reInstallProfile]);

  return device?.detail &&
    application &&
    displayName &&
    notification !== undefined ? (
    <DefaultTemplate
      headerText={intl.formatMessage({
        id: localeIds.appDetailPageLayout.title,
      })}
      onBack={(): void => {
        history.push(ROUTER_PATH.apps);
      }}
    >
      <DistributionDetailPageLayout
        deviceDetail={device.detail}
        application={application}
        displayName={displayName}
        notification={notification}
        commentListPath={getRequestPath(ROUTER_PATH.distributionComments, {
          distributionAccessKey,
        })}
        startInstall={startInstall}
        startOverwriteInstall={startOverwriteInstall}
        userName={device?.user?.name}
        notificationEmail={device?.email}
        isLogin={deviceDomain.isAuthenticated()}
        onInstall={handleInstall}
        onOverwriteInstall={handleOverwriteInstall}
        onUnsupportedOverwriteInstall={handleUnsupportedOverwriteInstall}
        onChangeDisplayName={handleChangeDisplayName}
        onLeave={handleLeave}
        onChangeNotification={handleChangeNotification}
        installReady={!!installSessionToken}
        onReInstallProfile={handleReInstallProfile}
      />
    </DefaultTemplate>
  ) : (
    <DefaultTemplate headerText="">
      <Splash />
    </DefaultTemplate>
  );
};
